import { submitBookingOnboarding } from '@/_apis_/booking/axios';
import { CommonInput } from '@/components/_dashboard/promotion/formik/commonInput';
import {
    BOOKING_ONBOARDING_BOOKING_TYPE_OPTIONS,
    BOOKING_ONBOARDING_BOOKING_TYPE_LABEL_KEYS,
    BOOKING_ONBOARDING_FIELD_CONTENT,
    BOOKING_ONBOARDING_INITIAL_VALUES,
    BOOKING_ONBOARDING_TIMEZONE_OPTIONS
} from '@/constants/bookingOnboarding';
import { Turnstile } from '@marsidev/react-turnstile';
import type { TurnstileInstance } from '@marsidev/react-turnstile';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import RestaurantMenuOutlinedIcon from '@mui/icons-material/RestaurantMenuOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import SettingsSuggestOutlinedIcon from '@mui/icons-material/SettingsSuggestOutlined';
import {
    Alert,
    Autocomplete,
    Box,
    Container,
    FormControl,
    FormControlLabel,
    FormHelperText,
    FormLabel,
    Grid,
    Paper,
    Radio,
    RadioGroup,
    Stack,
    TextField,
    TextFieldProps,
    Typography
} from '@mui/material';
import LoadingButton from '@mui/material/Button';
import { Form, FormikProvider, useFormik } from 'formik';
import { MuiTelInput } from 'mui-tel-input';
import { type ChangeEvent, type ReactNode, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bookingOnboardingValidationSchema } from './schema';
import { createBookingOnboardingSubmissionPayload } from './submissionFields';
import { BookingOnboardingFormValues } from './types';

const BOOKING_ONBOARDING_TURNSTILE_SITE_KEY = import.meta.env.VITE_CLOUDFLARE_TURNSTILE_KEY;
const BOOKING_ONBOARDING_REQUIRED_FIELD_COUNT = 11;
const BOOKING_ONBOARDING_SETUP_AREA_COUNT = 3;
const BOOKING_ONBOARDING_TEAM_REVIEW_COUNT = 1;

const FORM_SECTION_MARKERS = [
    {
        labelKey: 'bookingOnboarding.sections.restaurantProfile.label',
        icon: <PersonOutlineIcon fontSize="small" />,
        fieldKeys: [
            'bookingOnboarding.sections.restaurantProfile.name',
            'bookingOnboarding.sections.restaurantProfile.contact',
            'bookingOnboarding.sections.restaurantProfile.manager'
        ]
    },
    {
        labelKey: 'bookingOnboarding.sections.bookingRules.label',
        icon: <ScheduleOutlinedIcon fontSize="small" />,
        fieldKeys: [
            'bookingOnboarding.sections.bookingRules.duration',
            'bookingOnboarding.sections.bookingRules.timeSlots',
            'bookingOnboarding.sections.bookingRules.advanceNotice'
        ]
    },
    {
        labelKey: 'bookingOnboarding.sections.capacityAssignment.label',
        icon: <GroupsOutlinedIcon fontSize="small" />,
        fieldKeys: [
            'bookingOnboarding.sections.capacityAssignment.capacity',
            'bookingOnboarding.sections.capacityAssignment.timeZone',
            'bookingOnboarding.sections.capacityAssignment.paxLimit'
        ]
    }
] as const;

const fieldProps = (
    formik: ReturnType<typeof useFormik<BookingOnboardingFormValues>>,
    name: keyof BookingOnboardingFormValues
): Pick<TextFieldProps, 'name' | 'value' | 'onBlur' | 'error' | 'helperText'> => {
    const touched = Boolean(formik.touched[name]);
    const error = formik.errors[name];

    return {
        name,
        value: formik.values[name],
        onBlur: formik.handleBlur,
        error: touched && Boolean(error),
        helperText: touched && error ? error : undefined
    };
};

interface QuestionCardProps {
    index: number;
    title: string;
    helper: readonly string[];
    icon: ReactNode;
    required?: boolean;
    children: ReactNode;
}

const QuestionCard = ({ index, title, helper, icon, required = true, children }: QuestionCardProps): JSX.Element => {
    const { t } = useTranslation();

    return (
        <Paper
            component="section"
            variant="outlined"
            sx={{
                position: 'relative',
                overflow: 'hidden',
                p: { xs: 2.25, sm: 3 },
                borderRadius: 1,
                borderColor: (theme) => theme.palette.divider,
                boxShadow: (theme) => theme.customShadows.z8,
                animation: 'bookingOnboardingCardIn 360ms ease-out',
                animationDelay: `${Math.min(index * 35, 240)}ms`,
                transition: (theme) =>
                    theme.transitions.create(['border-color', 'box-shadow', 'transform'], {
                        duration: theme.transitions.duration.shorter
                    }),
                '&:before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 4,
                    height: '100%',
                    bgcolor: 'info.main',
                    opacity: 0.72
                },
                '&:hover': {
                    transform: { md: 'translateY(-2px)' },
                    borderColor: 'info.light',
                    boxShadow: (theme) => theme.customShadows.z16
                },
                '@keyframes bookingOnboardingCardIn': {
                    from: {
                        transform: 'translateY(12px)'
                    },
                    to: {
                        transform: 'translateY(0)'
                    }
                },
                '@media (prefers-reduced-motion: reduce)': {
                    animation: 'none',
                    transition: 'none',
                    opacity: 1,
                    transform: 'none'
                }
            }}
        >
            <Stack spacing={2}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    <Box
                        sx={{
                            flexShrink: 0,
                            width: 42,
                            height: 42,
                            borderRadius: 1,
                            display: 'grid',
                            placeItems: 'center',
                            color: 'info.main',
                            bgcolor: (theme) => theme.palette.info.lighter,
                            border: (theme) => `1px solid ${theme.palette.info.light}`
                        }}
                    >
                        {icon}
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.75 }}>
                            <Typography
                                variant="caption"
                                sx={{
                                    px: 1,
                                    py: 0.35,
                                    borderRadius: 0.75,
                                    color: 'text.secondary',
                                    bgcolor: 'background.neutral',
                                    fontWeight: 700
                                }}
                            >
                                {String(index + 1).padStart(2, '0')}
                            </Typography>
                            {required && (
                                <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 700 }}>
                                    {t('bookingOnboarding.requiredLabel')}
                                </Typography>
                            )}
                        </Stack>
                        <Typography variant="subtitle1" component="h2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                            {title}
                        </Typography>
                        <Stack spacing={0.5} sx={{ mt: 0.75, maxWidth: 720 }}>
                            {helper.map((line) => (
                                <Typography key={line} variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.55 }}>
                                    {line}
                                </Typography>
                            ))}
                        </Stack>
                    </Box>
                </Stack>
                {children}
            </Stack>
        </Paper>
    );
};

interface SummaryMetricProps {
    value: string;
    label: string;
}

const SummaryMetric = ({ value, label }: SummaryMetricProps): JSX.Element => (
    <Box
        sx={{
            p: 1.5,
            borderRadius: 1,
            bgcolor: 'background.neutral',
            border: (theme) => `1px solid ${theme.palette.divider}`
        }}
    >
        <Typography variant="h4" sx={{ color: 'text.primary', lineHeight: 1 }}>
            {value}
        </Typography>
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {label}
        </Typography>
    </Box>
);

type BookingOnboardingFieldContent = (typeof BOOKING_ONBOARDING_FIELD_CONTENT)[keyof typeof BOOKING_ONBOARDING_FIELD_CONTENT];
type QuestionCardCopy = Pick<QuestionCardProps, 'title' | 'helper'>;
type BookingOnboardingNumericFieldName =
    | 'diningDuration'
    | 'bookingSlotInterval'
    | 'minimumAdvanceBookingTime'
    | 'sessionMaxCapacity'
    | 'maxPaxPerBooking';

const BookingOnboarding = (): JSX.Element => {
    const { t, i18n } = useTranslation();
    const countryNameLanguage = i18n.language.startsWith('zh') ? 'zh' : 'en';
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
    const turnstileRef = useRef<TurnstileInstance>(null);

    const resetTurnstile = (): void => {
        setTurnstileToken(null);
        turnstileRef.current?.reset();
    };

    const formik = useFormik<BookingOnboardingFormValues>({
        initialValues: BOOKING_ONBOARDING_INITIAL_VALUES,
        validationSchema: bookingOnboardingValidationSchema,
        onSubmit: async (values, { setSubmitting }) => {
            setSubmitError('');
            if (!BOOKING_ONBOARDING_TURNSTILE_SITE_KEY) {
                setSubmitError(t('bookingOnboarding.turnstileKeyError'));
                setSubmitting(false);
                return;
            }
            if (!turnstileToken) {
                setSubmitError(t('bookingOnboarding.submitError'));
                setSubmitting(false);
                return;
            }

            try {
                const response = await submitBookingOnboarding(createBookingOnboardingSubmissionPayload(values), turnstileToken);

                if (!response?.data) {
                    throw new Error(response?.message || t('bookingOnboarding.submitError'));
                }

                setIsSubmitted(true);
            } catch {
                resetTurnstile();
                setSubmitError(t('bookingOnboarding.submitError'));
            } finally {
                setSubmitting(false);
            }
        }
    });

    const getQuestionCardCopy = (content: BookingOnboardingFieldContent): QuestionCardCopy => ({
        title: t(content.titleKey),
        helper: content.helperKeys.map((key) => t(key))
    });

    const handlePositiveIntegerFieldChange =
        (fieldName: BookingOnboardingNumericFieldName) =>
        (event: ChangeEvent<HTMLInputElement>): void => {
            const inputValue = event.target.value.trim();

            if (inputValue === '') {
                void formik.setFieldValue(fieldName, '');
                return;
            }
            if (!/^\d+$/.test(inputValue)) return;

            const sanitizedValue = inputValue.replace(/^0+(?=\d)/, '');
            void formik.setFieldValue(fieldName, sanitizedValue);
            event.target.value = sanitizedValue;
        };

    if (isSubmitted) {
        return (
            <Box
                component="main"
                sx={{
                    minHeight: '100vh',
                    background: (theme) =>
                        `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.neutral} 100%)`,
                    pt: { xs: 12, sm: 16 },
                    pb: { xs: 6, md: 10 }
                }}
            >
                <Container maxWidth="sm">
                    <Paper
                        elevation={0}
                        sx={{
                            p: { xs: 3, sm: 5 },
                            borderRadius: 1,
                            textAlign: 'center',
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            boxShadow: (theme) => theme.customShadows.z16
                        }}
                    >
                        <CheckCircleOutlineIcon sx={{ fontSize: 64, mb: 2, color: 'info.main' }} />
                        <Typography variant="h3" sx={{ mb: 2, color: 'text.primary' }}>
                            {t('bookingOnboarding.successTitle')}
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                            {t('bookingOnboarding.successDescription')}
                        </Typography>
                        <Alert
                            severity="info"
                            icon={<MapOutlinedIcon />}
                            sx={{
                                border: (theme) => `1px solid ${theme.palette.divider}`
                            }}
                        >
                            {t('bookingOnboarding.floormapReminder')}
                        </Alert>
                    </Paper>
                </Container>
            </Box>
        );
    }

    return (
        <Box
            component="main"
            sx={{
                position: 'relative',
                overflow: 'hidden',
                minHeight: '100vh',
                background: (theme) =>
                    `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.neutral} 100%)`,
                pt: { xs: 11, sm: 15 },
                pb: { xs: 5, md: 8 },
                '&:before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    background: (theme) =>
                        `linear-gradient(90deg, ${theme.palette.info.lighter} 0, transparent 34%), radial-gradient(circle at 85% 12%, ${theme.palette.background.paper} 0, transparent 30%)`,
                    opacity: 0.54,
                    pointerEvents: 'none'
                }
            }}
        >
            <Container maxWidth="lg" sx={{ position: 'relative' }}>
                <Grid container spacing={{ xs: 3, md: 4 }} alignItems="flex-start">
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Stack
                            component="aside"
                            spacing={3}
                            sx={{
                                position: { md: 'sticky' },
                                top: { md: 96 },
                                animation: 'bookingOnboardingSidebarIn 420ms ease-out',
                                '@keyframes bookingOnboardingSidebarIn': {
                                    from: {
                                        transform: 'translateY(12px)'
                                    },
                                    to: {
                                        transform: 'translateY(0)'
                                    }
                                },
                                '@media (prefers-reduced-motion: reduce)': {
                                    animation: 'none',
                                    opacity: 1,
                                    transform: 'none'
                                }
                            }}
                        >
                            <Paper
                                elevation={0}
                                sx={{
                                    p: { xs: 2.5, sm: 3 },
                                    borderRadius: 1,
                                    border: (theme) => `1px solid ${theme.palette.divider}`,
                                    boxShadow: (theme) => theme.customShadows.z16,
                                    bgcolor: (theme) => theme.palette.background.paper
                                }}
                            >
                                <Typography
                                    variant="overline"
                                    sx={{
                                        color: 'info.main',
                                        fontWeight: 800,
                                        letterSpacing: 1.4
                                    }}
                                >
                                    {t('bookingOnboarding.eyebrow')}
                                </Typography>
                                <Typography variant="h2" component="h1" sx={{ mt: 1, mb: 2, color: 'text.primary' }}>
                                    {t('bookingOnboarding.title')}
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>
                                    {t('bookingOnboarding.description1')}
                                </Typography>
                                <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                                    {t('bookingOnboarding.description2')}
                                </Typography>

                                <Grid container spacing={1.25} sx={{ mt: 3 }}>
                                    <Grid size={{ xs: 4 }}>
                                        <SummaryMetric
                                            value={String(BOOKING_ONBOARDING_REQUIRED_FIELD_COUNT)}
                                            label={t('bookingOnboarding.requiredFieldsMetric')}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <SummaryMetric
                                            value={String(BOOKING_ONBOARDING_SETUP_AREA_COUNT)}
                                            label={t('bookingOnboarding.setupAreasMetric')}
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 4 }}>
                                        <SummaryMetric
                                            value={String(BOOKING_ONBOARDING_TEAM_REVIEW_COUNT)}
                                            label={t('bookingOnboarding.teamReviewMetric')}
                                        />
                                    </Grid>
                                </Grid>
                            </Paper>

                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 2.25,
                                    borderRadius: 1,
                                    boxShadow: (theme) => theme.customShadows.z8,
                                    bgcolor: (theme) => theme.palette.background.paper
                                }}
                            >
                                <Stack spacing={1.75}>
                                    <Typography variant="subtitle2" sx={{ color: 'text.primary', fontWeight: 800 }}>
                                        {t('bookingOnboarding.setupSummaryTitle')}
                                    </Typography>
                                    {FORM_SECTION_MARKERS.map((section) => (
                                        <Stack key={section.labelKey} direction="row" spacing={1.5} alignItems="flex-start">
                                            <Box
                                                sx={{
                                                    width: 34,
                                                    height: 34,
                                                    flexShrink: 0,
                                                    borderRadius: 1,
                                                    display: 'grid',
                                                    placeItems: 'center',
                                                    color: 'info.main',
                                                    bgcolor: 'background.neutral',
                                                    border: (theme) => `1px solid ${theme.palette.divider}`
                                                }}
                                            >
                                                {section.icon}
                                            </Box>
                                            <Box>
                                                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
                                                    {t(section.labelKey)}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                                    {section.fieldKeys.map((fieldKey) => t(fieldKey)).join(' / ')}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    ))}
                                </Stack>
                            </Paper>

                            <Stack spacing={2}>
                                <Paper
                                    variant="outlined"
                                    sx={{
                                        p: 2,
                                        borderRadius: 1,
                                        boxShadow: (theme) => theme.customShadows.z8,
                                        bgcolor: (theme) => theme.palette.background.paper
                                    }}
                                >
                                    <Stack direction="row" spacing={1.5}>
                                        <EventAvailableOutlinedIcon sx={{ color: 'info.main' }} />
                                        <Box>
                                            <Typography variant="subtitle2" sx={{ color: 'text.primary' }}>
                                                {t('bookingOnboarding.bookingRulesCardTitle')}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                                {t('bookingOnboarding.bookingRulesCardDescription')}
                                            </Typography>
                                        </Box>
                                    </Stack>
                                </Paper>
                            </Stack>
                        </Stack>
                    </Grid>

                    <Grid size={{ xs: 12, md: 7 }}>
                        <FormikProvider value={formik}>
                            <Form noValidate onSubmit={formik.handleSubmit}>
                                <Stack spacing={2}>
                                    <Paper
                                        component="div"
                                        elevation={0}
                                        sx={{
                                            p: { xs: 2.25, sm: 3 },
                                            borderRadius: 1,
                                            border: (theme) => `1px solid ${theme.palette.divider}`,
                                            boxShadow: (theme) => theme.customShadows.z16,
                                            bgcolor: (theme) => theme.palette.background.paper,
                                            animation: 'bookingOnboardingHeaderIn 380ms ease-out',
                                            '@keyframes bookingOnboardingHeaderIn': {
                                                from: {
                                                    transform: 'translateY(10px)'
                                                },
                                                to: {
                                                    transform: 'translateY(0)'
                                                }
                                            },
                                            '@media (prefers-reduced-motion: reduce)': {
                                                animation: 'none',
                                                opacity: 1,
                                                transform: 'none'
                                            }
                                        }}
                                    >
                                        <Stack
                                            direction={{ xs: 'column', sm: 'row' }}
                                            spacing={2}
                                            alignItems={{ xs: 'flex-start', sm: 'center' }}
                                            justifyContent="space-between"
                                        >
                                            <Box>
                                                <Typography variant="h4" sx={{ color: 'text.primary', mb: 0.75 }}>
                                                    {t('bookingOnboarding.formHeaderTitle')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 640 }}>
                                                    {t('bookingOnboarding.formHeaderDescription')}
                                                </Typography>
                                            </Box>
                                            <Box
                                                sx={{
                                                    px: 1.25,
                                                    py: 0.75,
                                                    borderRadius: 1,
                                                    color: 'info.main',
                                                    bgcolor: (theme) => theme.palette.info.lighter,
                                                    fontWeight: 800,
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {t('bookingOnboarding.fieldCount', {
                                                    count: BOOKING_ONBOARDING_REQUIRED_FIELD_COUNT
                                                })}
                                            </Box>
                                        </Stack>
                                    </Paper>

                                    {submitError && <Alert severity="error">{submitError}</Alert>}

                                    <QuestionCard
                                        index={0}
                                        icon={<RestaurantMenuOutlinedIcon />}
                                        {...getQuestionCardCopy(BOOKING_ONBOARDING_FIELD_CONTENT.restaurantName)}
                                    >
                                        <TextField
                                            fullWidth
                                            required
                                            placeholder={t('bookingOnboarding.restaurantNamePlaceholder')}
                                            onChange={formik.handleChange}
                                            {...fieldProps(formik, 'restaurantName')}
                                        />
                                    </QuestionCard>

                                    <QuestionCard
                                        index={1}
                                        icon={<PersonOutlineIcon />}
                                        {...getQuestionCardCopy(BOOKING_ONBOARDING_FIELD_CONTENT.email)}
                                    >
                                        <TextField
                                            fullWidth
                                            required
                                            type="email"
                                            placeholder={t('bookingOnboarding.emailPlaceholder')}
                                            onChange={formik.handleChange}
                                            {...fieldProps(formik, 'email')}
                                        />
                                    </QuestionCard>

                                    <QuestionCard
                                        index={2}
                                        icon={<PersonOutlineIcon />}
                                        {...getQuestionCardCopy(BOOKING_ONBOARDING_FIELD_CONTENT.contact)}
                                    >
                                        <MuiTelInput
                                            fullWidth
                                            required
                                            value={formik.values.contact}
                                            onChange={(value, info) => {
                                                formik.setFieldValue('contact', value);
                                                if (info.countryCode) {
                                                    formik.setFieldValue('contactCountry', info.countryCode);
                                                }
                                            }}
                                            onBlur={() => {
                                                formik.setFieldTouched('contact', true);
                                            }}
                                            defaultCountry={formik.values.contactCountry}
                                            preferredCountries={[formik.values.contactCountry]}
                                            forceCallingCode
                                            disableFormatting
                                            langOfCountryName={countryNameLanguage}
                                            placeholder={t('bookingOnboarding.contactPlaceholder')}
                                            error={Boolean(formik.touched.contact && formik.errors.contact)}
                                            helperText={formik.touched.contact && formik.errors.contact ? formik.errors.contact : undefined}
                                            MenuProps={{
                                                PaperProps: {
                                                    sx: {
                                                        maxHeight: 360
                                                    }
                                                }
                                            }}
                                        />
                                    </QuestionCard>

                                    <QuestionCard
                                        index={3}
                                        icon={<PersonOutlineIcon />}
                                        {...getQuestionCardCopy(BOOKING_ONBOARDING_FIELD_CONTENT.ownerManagerName)}
                                    >
                                        <TextField
                                            fullWidth
                                            required
                                            placeholder={t('bookingOnboarding.ownerManagerNamePlaceholder')}
                                            onChange={formik.handleChange}
                                            {...fieldProps(formik, 'ownerManagerName')}
                                        />
                                    </QuestionCard>

                                    <QuestionCard
                                        index={4}
                                        icon={<ScheduleOutlinedIcon />}
                                        {...getQuestionCardCopy(BOOKING_ONBOARDING_FIELD_CONTENT.diningDuration)}
                                    >
                                        <CommonInput
                                            formik={formik}
                                            fieldPath="diningDuration"
                                            type="number"
                                            placeholder="bookingOnboarding.diningDurationPlaceholder"
                                            endAdornment="mins"
                                            handleOnChange={handlePositiveIntegerFieldChange('diningDuration')}
                                        />
                                    </QuestionCard>

                                    <QuestionCard
                                        index={5}
                                        icon={<EventAvailableOutlinedIcon />}
                                        {...getQuestionCardCopy(BOOKING_ONBOARDING_FIELD_CONTENT.bookingSlotInterval)}
                                    >
                                        <CommonInput
                                            formik={formik}
                                            fieldPath="bookingSlotInterval"
                                            type="number"
                                            placeholder="bookingOnboarding.bookingSlotIntervalPlaceholder"
                                            endAdornment="mins"
                                            handleOnChange={handlePositiveIntegerFieldChange('bookingSlotInterval')}
                                        />
                                    </QuestionCard>

                                    <QuestionCard
                                        index={6}
                                        icon={<ScheduleOutlinedIcon />}
                                        {...getQuestionCardCopy(BOOKING_ONBOARDING_FIELD_CONTENT.minimumAdvanceBookingTime)}
                                    >
                                        <CommonInput
                                            formik={formik}
                                            fieldPath="minimumAdvanceBookingTime"
                                            type="number"
                                            placeholder="bookingOnboarding.minimumAdvanceBookingTimePlaceholder"
                                            endAdornment="mins"
                                            handleOnChange={handlePositiveIntegerFieldChange('minimumAdvanceBookingTime')}
                                        />
                                    </QuestionCard>

                                    <QuestionCard
                                        index={7}
                                        icon={<GroupsOutlinedIcon />}
                                        {...getQuestionCardCopy(BOOKING_ONBOARDING_FIELD_CONTENT.sessionMaxCapacity)}
                                    >
                                        <CommonInput
                                            formik={formik}
                                            fieldPath="sessionMaxCapacity"
                                            type="number"
                                            placeholder="bookingOnboarding.sessionMaxCapacityPlaceholder"
                                            endAdornment="guests"
                                            handleOnChange={handlePositiveIntegerFieldChange('sessionMaxCapacity')}
                                        />
                                    </QuestionCard>

                                    <QuestionCard
                                        index={8}
                                        icon={<ScheduleOutlinedIcon />}
                                        {...getQuestionCardCopy(BOOKING_ONBOARDING_FIELD_CONTENT.timezone)}
                                    >
                                        <Autocomplete
                                            options={[...BOOKING_ONBOARDING_TIMEZONE_OPTIONS].sort(
                                                (a, b) => -b.firstLetter.localeCompare(a.firstLetter)
                                            )}
                                            getOptionLabel={(option) => option.label}
                                            value={
                                                BOOKING_ONBOARDING_TIMEZONE_OPTIONS.find(
                                                    (item) => item.tzCode === formik.values.timezone
                                                ) || null
                                            }
                                            onChange={(event, value) => formik.setFieldValue('timezone', value?.tzCode || '')}
                                            onBlur={() => formik.setFieldTouched('timezone', true)}
                                            renderInput={(params) => (
                                                <TextField
                                                    {...params}
                                                    required
                                                    placeholder={t('bookingOnboarding.timezonePlaceholder')}
                                                    error={Boolean(formik.touched.timezone && formik.errors.timezone)}
                                                    helperText={
                                                        formik.touched.timezone && formik.errors.timezone
                                                            ? formik.errors.timezone
                                                            : undefined
                                                    }
                                                />
                                            )}
                                        />
                                    </QuestionCard>

                                    <QuestionCard
                                        index={9}
                                        icon={<SettingsSuggestOutlinedIcon />}
                                        {...getQuestionCardCopy(BOOKING_ONBOARDING_FIELD_CONTENT.bookingType)}
                                    >
                                        <FormControl fullWidth error={Boolean(formik.touched.bookingType && formik.errors.bookingType)}>
                                            <FormLabel sx={{ display: 'none' }}>
                                                {t(BOOKING_ONBOARDING_FIELD_CONTENT.bookingType.titleKey)}
                                            </FormLabel>
                                            <RadioGroup
                                                name="bookingType"
                                                value={formik.values.bookingType}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                            >
                                                {BOOKING_ONBOARDING_BOOKING_TYPE_OPTIONS.map((bookingType) => (
                                                    <FormControlLabel
                                                        key={bookingType}
                                                        value={bookingType}
                                                        control={<Radio />}
                                                        label={t(BOOKING_ONBOARDING_BOOKING_TYPE_LABEL_KEYS[bookingType])}
                                                    />
                                                ))}
                                            </RadioGroup>
                                            {formik.touched.bookingType && formik.errors.bookingType && (
                                                <FormHelperText>{formik.errors.bookingType}</FormHelperText>
                                            )}
                                        </FormControl>
                                    </QuestionCard>

                                    <QuestionCard
                                        index={10}
                                        icon={<GroupsOutlinedIcon />}
                                        {...getQuestionCardCopy(BOOKING_ONBOARDING_FIELD_CONTENT.maxPaxPerBooking)}
                                    >
                                        <CommonInput
                                            formik={formik}
                                            fieldPath="maxPaxPerBooking"
                                            type="number"
                                            placeholder="bookingOnboarding.maxPaxPerBookingPlaceholder"
                                            endAdornment="guests"
                                            handleOnChange={handlePositiveIntegerFieldChange('maxPaxPerBooking')}
                                        />
                                    </QuestionCard>

                                    {BOOKING_ONBOARDING_TURNSTILE_SITE_KEY && (
                                        <Turnstile
                                            ref={turnstileRef}
                                            siteKey={BOOKING_ONBOARDING_TURNSTILE_SITE_KEY}
                                            onSuccess={setTurnstileToken}
                                            onExpire={() => setTurnstileToken(null)}
                                            onError={() => setTurnstileToken(null)}
                                        />
                                    )}

                                    <LoadingButton
                                        fullWidth
                                        size="large"
                                        type="submit"
                                        variant="contained"
                                        loading={formik.isSubmitting}
                                        disabled={Boolean(BOOKING_ONBOARDING_TURNSTILE_SITE_KEY) && !turnstileToken}
                                        sx={{
                                            height: 48,
                                            fontWeight: 700
                                        }}
                                    >
                                        {t('bookingOnboarding.submit')}
                                    </LoadingButton>
                                </Stack>
                            </Form>
                        </FormikProvider>
                    </Grid>
                </Grid>
            </Container>
        </Box>
    );
};

export default BookingOnboarding;
